import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

/**
 * SPA landing page for invite links of the form:
 *
 *     https://intel.cyberfind.io/invite?idp=<provider>#token=<token>
 *
 * The single-use invite token is delivered in the URL *fragment* (never the
 * query string) so it is never sent to the server as part of a normal request
 * and never lands in server / proxy access logs [R4]. This page:
 *
 *   1. reads the token from `location.hash` and the operator-pinned IdP from
 *      the query string,
 *   2. immediately strips the fragment from the address bar / history so the
 *      token does not linger after we've read it,
 *   3. POSTs the token to `/api/auth/redeem-stash`, which stashes it in the
 *      server session (and mints the binding nonce), then
 *   4. full-page-navigates to `/api/auth/login?idp=<provider>` to begin OIDC.
 *
 * The token is never rendered, logged, or placed in the DOM.
 *
 * NOTE: this builds against backend contracts that land in separate PRs — the
 * `POST /api/auth/redeem-stash` endpoint and the binding-gate deny reason
 * codes on the login page. It is inert until the `binding_gate_enforced` flag
 * is enabled at cutover.
 */
export function InviteRedeemPage() {
  // Guards against React 18 StrictMode's double-invoke so we redeem once.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const token = new URLSearchParams(
      window.location.hash.replace(/^#/, ""),
    ).get("token");
    const idp = new URLSearchParams(window.location.search).get("idp");

    // Strip the fragment (and thus the token) from the URL / history *before*
    // the network round-trip, so it never lingers in the address bar.
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );

    if (!token) {
      // Nothing to redeem — fall back to the normal sign-in surface.
      window.location.assign("/login");
      return;
    }

    api
      .post<{ nonce?: string }>("/auth/redeem-stash", { token })
      .then((res) => {
        // Forward the single-use binding nonce (returned by redeem-stash) to the
        // login route as `bn=`, plus the operator-pinned IdP. /api/auth/login
        // threads the nonce through the OIDC `state` so the callback can match
        // it against the session's stored hash (anti login-CSRF). The nonce is a
        // CSRF value, not the invite token — the token stays server-side.
        const params = new URLSearchParams();
        if (idp) params.set("idp", idp);
        if (res?.nonce) params.set("bn", res.nonce);
        const qs = params.toString();
        // Full-page navigation: hands off to the Express OIDC login route,
        // which 302s to ZITADEL. Must not be a client-side route change.
        window.location.assign(`/api/auth/login${qs ? `?${qs}` : ""}`);
      })
      .catch(() => {
        // Redeem failed (expired / reused / invalid). Surface the generic
        // binding prompt on the login page; the specific reason code arrives
        // from the backend on the real deny paths.
        window.location.assign("/login?error=binding_required");
      });
  }, []);

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background p-6"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <span
          className="h-6 w-6 animate-pulse rounded-full bg-muted"
          aria-hidden="true"
        />
        <p className="text-sm text-muted-foreground">
          Preparing your secure sign-in&hellip;
        </p>
      </div>
    </div>
  );
}
